import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const SERVICE_OPTIONS = [
  "Personal Assistance Services (CHC)", "Home Care Assistance (Veteran's Benefit)",
  "Personal Care (Private)", "Long Term Care Services (LTC Insurance)",
  "Companionship (Private)", "Housekeeping (Private)", "Transportation (Private)",
];

const FIELDS = [
  ["effective_date", "Effective date", "date"],
  ["client_name", "Client full name", "text"],
  ["client_address", "Client address", "text"],
  ["authorized_person", "Authorized person for care decisions", "text"],
  ["authorized_type", "Authorized person type", "text"],
  ["emergency_phone", "Emergency contact phone", "text"],
  ["email", "Client email", "text"],
  ["start_date", "Service start date", "date"],
  ["rate_amount", "Rate ($)", "text"],
];

const SHIFTS = [1, 2, 3];

export const AgreementCard = ({ clientId, onChanged }) => {
  const [agr, setAgr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${clientId}/agreement`).then((r) => setAgr(r.data.agreement)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  if (!agr) return null;
  const signed = agr.signed?.signed_at;

  const set = (k) => (e) => setAgr({ ...agr, [k]: e.target.value });

  const toggleService = (s) => {
    const cur = new Set(agr.services || []);
    if (cur.has(s)) cur.delete(s); else cur.add(s);
    setAgr({ ...agr, services: Array.from(cur) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/agreement`, agr);
      if (data.signature_reset) toast.warning("Agreement changed after signing — the client will need to sign again.");
      toast.success("Agreement saved — the client can now review & sign it from their intake link");
      setAgr(data.agreement);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/agreement/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="agreement-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Current Service Agreement & Authorization for Care</p>
          <p className="mt-0.5 text-xs text-stone-500">Fillable office form prefilled from the intake questionnaire and earliest imported service date. The PDF uses the current uploaded agreement template.</p>
        </div>
        {signed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1 text-xs font-medium text-[#1e8449]" data-testid="agreement-signed-badge">
            <CheckCircle size={14} weight="fill" /> Signed by {agr.signed.signer_name} {String(signed).slice(0, 10)}
          </span>
        ) : (
          <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-3 py-1 text-xs font-medium text-[#8a5d14]" data-testid="agreement-unsigned-badge">Awaiting client signature</span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className="space-y-1.5">
            <Label>{label}</Label>
            <Input type={type} value={agr[k] || ""} onChange={set(k)} className="border-stone-300 bg-white" data-testid={`agreement-${k}`} />
          </div>
        ))}
        <div className="space-y-1.5">
          <Label>Rate unit</Label>
          <select value={agr.rate_unit || "Hourly"} onChange={set("rate_unit")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="agreement-rate-unit">
            {["Hourly", "Per Visit", "Weekly"].map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Payment method</Label>
          <select value={agr.payment_method || ""} onChange={set("payment_method")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="agreement-payment-method">
            <option value="">—</option>
            {["Check by mail", "Cash App", "Venmo", "Credit card (office collects by phone)", "CHC / Medicaid — not billed to client"].map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Autopay enrollment</Label>
          <select value={agr.autopay || ""} onChange={set("autopay")} className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="agreement-autopay">
            <option value="">—</option><option>Yes</option><option>No</option>
          </select>
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label>Home care services required</Label>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2" data-testid="agreement-services">
          {SERVICE_OPTIONS.map((s) => (
            <label key={s} className="flex items-center gap-1.5 text-xs text-stone-600">
              <input type="checkbox" checked={(agr.services || []).includes(s)} onChange={() => toggleService(s)} data-testid={`agreement-svc-${s.slice(0, 10).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
              {s}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {SHIFTS.map((n) => (
          <div key={n} className="space-y-2 rounded-lg border border-stone-100 p-3">
            <p className="text-xs font-semibold text-stone-500">Shift {n}</p>
            <Input placeholder="Days" value={agr[`shift${n}_days`] || ""} onChange={set(`shift${n}_days`)} className="h-8 border-stone-300 bg-white text-xs" data-testid={`agreement-shift${n}-days`} />
            <Input placeholder="Time" value={agr[`shift${n}_time`] || ""} onChange={set(`shift${n}_time`)} className="h-8 border-stone-300 bg-white text-xs" data-testid={`agreement-shift${n}-time`} />
            <Input placeholder="Caregiver (if known)" value={agr[`shift${n}_caregiver`] || ""} onChange={set(`shift${n}_caregiver`)} className="h-8 border-stone-300 bg-white text-xs" data-testid={`agreement-shift${n}-caregiver`} />
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-1.5">
        <Label>Services notes</Label>
        <Input value={agr.services_notes || ""} onChange={set("services_notes")} className="border-stone-300 bg-white" data-testid="agreement-services-notes" />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button asChild variant="outline" className="border-stone-300" data-testid="agreement-full-form"><Link to={`/clients/${clientId}/service-agreement`}>Open full form</Link></Button>
        <Button variant="outline" onClick={preview} className="border-stone-300" data-testid="agreement-preview">Preview PDF</Button>
        <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="agreement-save">
          {busy ? "Saving…" : "Save agreement"}
        </Button>
      </div>
    </div>
  );
};
